import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { ProfileMenu } from './ProfileMenu'
import {
  Bolt, Notifications, DarkMode, ChevronRight
} from '@mui/icons-material'
import { buildAuthModalPath, refreshAuthProfile, resolveAuthToken, resolveAuthUser, resolveUserStage } from '../lib/utils'

interface Breadcrumb {
  label: string
  path?: string
}

interface HeaderProps {
  maxWidth?: string
  breadcrumbs?: Breadcrumb[]
  activeNav?: 'workspace' | 'import' | 'assets' | 'copyright' | 'pricing' | 'task-center'
  rightElement?: React.ReactNode
}

type NavItem = {
  key: string
  label: string
  path: string
  requiresAuth?: boolean
  requiresActive?: boolean
}

export const Header: React.FC<HeaderProps> = ({
  maxWidth = "max-w-6xl",
  breadcrumbs,
  activeNav,
  rightElement
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [authVersion, setAuthVersion] = useState(0)
  const moreMenuRef = useRef<HTMLDivElement | null>(null)
  const didRefreshRef = useRef(false)

  useEffect(() => {
    if (didRefreshRef.current) {
      return
    }
    didRefreshRef.current = true
    if (!resolveAuthToken()) {
      return
    }
    refreshAuthProfile().then((payload) => {
      if (payload) {
        setAuthVersion((prev) => prev + 1)
      }
    })
  }, [])

  useEffect(() => {
    const handleAuthUpdated = () => setAuthVersion((prev) => prev + 1)
    window.addEventListener('auth:updated', handleAuthUpdated)
    return () => window.removeEventListener('auth:updated', handleAuthUpdated)
  }, [])

  const userStage = useMemo(() => resolveUserStage(), [location.pathname, location.search, authVersion])
  const isAuthenticated = userStage.isAuthenticated
  const authUser = useMemo(() => resolveAuthUser(), [location.pathname, location.search, authVersion])
  const profileLabel = useMemo(() => {
    if (!authUser) {
      return ''
    }
    return authUser.nickname || authUser.username || authUser.email || authUser.phone || ''
  }, [authUser])
  const avatarText = profileLabel ? profileLabel.trim().charAt(0).toUpperCase() : ''
  const avatarUrl = authUser?.avatar || ''

  // Auto-detect active nav from current path
  const currentActiveNav = activeNav || (() => {
    if (location.pathname.startsWith('/copyright')) return 'copyright'
    if (location.pathname.startsWith('/pricing')) return 'pricing'
    if (location.pathname.startsWith('/assets')) return 'assets'
    if (location.pathname.startsWith('/task-center')) return 'task-center'
    if (location.pathname.startsWith('/import')) return 'import'
    return 'workspace'
  })()

  useEffect(() => {
    if (!isMoreOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (!moreMenuRef.current) return
      if (!moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMoreOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMoreOpen])

  const navItems = useMemo(() => {
    const base = {
      workspace: { key: 'workspace', label: t('profile.workspace'), path: '/workspace' },
      import: { key: 'import', label: t('import.nav'), path: '/import' },
      taskCenter: { key: 'task-center', label: t('taskCenter.nav'), path: '/task-center', requiresAuth: true },
      assets: { key: 'assets', label: t('assets.nav'), path: '/assets' },
      pricing: { key: 'pricing', label: t('profile.pricing'), path: '/pricing' },
      copyright: { key: 'copyright', label: t('copyright.nav'), path: '/copyright' }
    } satisfies Record<string, NavItem>

    if (!isAuthenticated) {
      const primary: NavItem[] = [base.workspace, base.import, base.pricing, base.taskCenter]
      return {
        primary,
        more: [] as NavItem[]
      }
    }

    if (userStage.stage === 'new') {
      const primary: NavItem[] = [base.workspace, base.import, base.pricing, base.taskCenter]
      const more: NavItem[] = [base.assets, base.copyright]
      return {
        primary,
        more
      }
    }

    const primary: NavItem[] = [base.workspace, base.taskCenter, base.assets, base.pricing, base.copyright]
    return {
      primary,
      more: [] as NavItem[]
    }
  }, [isAuthenticated, t, userStage.stage])

  const isMoreActive = navItems.more.some((item) => item.key === currentActiveNav)

  const openAuthModal = (mode: 'login' | 'signup') => {
    navigate(buildAuthModalPath(location.pathname, location.search, mode))
  }

  const handleNavClick = (item: { path: string; requiresAuth?: boolean; requiresActive?: boolean }) => {
    if (item.requiresAuth && !isAuthenticated) {
      openAuthModal('login')
      return
    }
    if (item.requiresActive && userStage.stage !== 'active') {
      return
    }
    navigate(item.path)
  }

  return (
    <header className="border-b border-slate-200 bg-white/80 sticky top-0 z-[60] backdrop-blur-md">
      <div className={`${maxWidth} mx-auto px-6 h-16 flex items-center justify-between`}>
        {/* Left Side */}
        <div className="flex items-center gap-2">
          <div onClick={() => navigate('/workspace')} className="cursor-pointer flex items-center gap-2 group">
            <div className="bg-primary p-1 rounded-lg text-white group-hover:bg-blue-700 transition-colors">
              <Bolt fontSize="small" />
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-900">{t('appName')} <span className="text-primary">AI</span></span>
          </div>

          <span className="mx-4 text-slate-300">|</span>

          {breadcrumbs ? (
            <nav className="flex items-center gap-2 text-sm font-medium">
              {breadcrumbs.map((crumb, index) => (
                <div key={index} className="flex items-center gap-2">
                  {index > 0 && <ChevronRight fontSize="small" className="text-slate-300" style={{ fontSize: 16 }} />}
                  <button
                    onClick={() => crumb.path && navigate(crumb.path)}
                    disabled={!crumb.path}
                    className={`${crumb.path ? 'text-slate-400 hover:text-primary cursor-pointer' : 'text-slate-900 cursor-default'} transition-colors`}
                  >
                    {crumb.label}
                  </button>
                </div>
              ))}
            </nav>
          ) : (
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
              {navItems.primary.map((item) => {
                const isActive = currentActiveNav === item.key
                const isDisabled =
                  (item.requiresAuth && !isAuthenticated) || (item.requiresActive && userStage.stage !== 'active')
                const hint = item.requiresAuth
                  ? t('appNav.loginHint')
                  : item.requiresActive && userStage.stage !== 'active'
                    ? t('appNav.unlockHint')
                    : undefined

                return (
                  <button
                    key={item.key}
                    onClick={() => handleNavClick(item)}
                    title={isDisabled ? hint : undefined}
                    className={`transition-colors flex items-center gap-1 border-b-2 pt-0.5 ${
                      isDisabled
                        ? 'text-slate-300 border-transparent cursor-not-allowed'
                        : isActive
                          ? 'text-primary font-bold border-primary'
                          : 'text-slate-500 hover:text-primary border-transparent'
                    }`}
                    disabled={isDisabled && !item.requiresAuth}
                  >
                    <span>{item.label}</span>
                    {isDisabled && <span className="material-symbols-outlined text-[14px]">lock</span>}
                  </button>
                )
              })}

              {navItems.more.length > 0 && (
                <div className="relative" ref={moreMenuRef}>
                  <button
                    onClick={() => setIsMoreOpen((prev) => !prev)}
                    className={`transition-colors flex items-center gap-1 border-b-2 pt-0.5 ${
                      isMoreActive ? 'text-primary font-bold border-primary' : 'text-slate-500 hover:text-primary border-transparent'
                    }`}
                  >
                    <span>{t('appNav.more')}</span>
                    <span className="material-symbols-outlined text-base">expand_more</span>
                  </button>
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 w-48 bg-white border border-slate-100 rounded-xl shadow-lg z-[70] overflow-hidden transition-all duration-200 ${
                      isMoreOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    {navItems.more.map((item) => {
                      const isDisabled = item.requiresActive && userStage.stage !== 'active'
                      const hint = isDisabled ? t('appNav.unlockHint') : undefined
                      return (
                        <button
                          key={item.key}
                          onClick={() => handleNavClick(item)}
                          title={hint}
                          className={`w-full px-4 py-3 text-left text-sm font-semibold transition-colors flex items-center justify-between ${
                            isDisabled ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-50'
                          }`}
                          disabled={isDisabled}
                        >
                          <span>{item.label}</span>
                          {isDisabled && <span className="material-symbols-outlined text-[16px]">lock</span>}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </nav>
          )}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {rightElement}

          <button className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors hidden sm:block">
            <DarkMode fontSize="small" />
          </button>

          <button className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors relative hidden sm:block">
            <Notifications fontSize="small" />
          </button>

          {isAuthenticated ? (
            <>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="w-9 h-9 rounded-full border border-slate-200 overflow-hidden hover:ring-2 hover:ring-primary/20 transition-all flex items-center justify-center bg-slate-100 text-slate-700 text-sm font-bold"
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt={profileLabel || 'avatar'} className="w-full h-full object-cover" />
                ) : avatarText ? (
                  <span>{avatarText}</span>
                ) : (
                  <span className="material-symbols-outlined text-base">person</span>
                )}
              </button>
              <ProfileMenu isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} />
            </>
          ) : (
            <button
              onClick={() => openAuthModal('login')}
              className="px-4 py-2 rounded-full border border-slate-200 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-primary hover:border-primary transition-all"
            >
              {t('nav.login')}
            </button>
          )}
        </div>
      </div>
    </header>
  )
}
